import { Router } from "@angular/router";
import { PaginatorState } from "src/app/shared/components/treeslo-grid/treeslo-paginator-cursor-based/treeslo-paginator-cursor-based.component";
import { TreesloGridSortSpec } from "src/app/shared/components/treeslo-grid/treeslo-grid.model";
import { RouteStore } from "src/app/shared/stores/route-store";
import { getRouteListContextData } from "./route-list-context.helper";

/** Router state key seeded by dashboard navigation to claim-type list views. */
export const CLAIM_TYPE_VIEW_ROUTE_NAVIGATION_STATE_KEY =
  "claimTypeViewRouteContext";

/**
 * View state persisted in RouteStore (sessionStorage "routeCache").
 * Used to restore filters, pagination and active row when returning from
 * Create Case or detail views.
 *
 * NOT related to ViewContextHistoryStateService — that service is only for
 * support/CRM audit trails (recordSupportHistory, recordListViewResolverContext).
 * Filter description helpers live in contact-support-history.helper.ts.
 */
export interface RouteViewStateData {
  filterForm?: Record<string, unknown>;
  paginatorState?: PaginatorState;
  activeRow?: number | null;
  expandedRow?: number | null;
  selectedRowIndexes?: number[];
  nestedOrderSelection?: {
    claimSku: string;
    selectedOrderIds: string[];
  };
  isOpenReferencesView?: boolean;
  sort?: TreesloGridSortSpec[];
  filtersAsideCollapsed?: boolean;
  /** Client-side paginator for secondary grids (e.g. export history). */
  historyPaginatorState?: PaginatorState;
}

/** @deprecated Use RouteViewStateData */
export type ClaimListViewContextData = RouteViewStateData;

export const CLAIM_LIST_VIEW_ROUTES = {
  damagedInventory: "claim/damaged-inventory",
  refundNeverReturned: "claim/refund-never-returned-claim",
  reAnalysis: "claim/reanalysis",
  lostOutbound: "claim/lost-outbound",
  storageFees: "claim/storage-fees",
} as const;

type RouteStoreInstance = InstanceType<typeof RouteStore>;

/** Reads saved view state from RouteStore for a list route URL. */
export function readRouteViewState(
  routeStore: RouteStoreInstance,
  routeUrl: string,
): RouteViewStateData | undefined {
  return getRouteListContextData<RouteViewStateData>(routeStore, routeUrl);
}

/**
 * Resolves claim-type list view state during navigation (e.g. dashboard → claim view).
 * Prefers router navigation state, then RouteStore context seeded before navigate.
 */
export function readClaimTypeListRouteViewState(
  routeStore: RouteStoreInstance,
  router: Router,
  routeUrl: string,
): RouteViewStateData | undefined {
  const navigationContext = router.getCurrentNavigation()?.extras?.state?.[
    CLAIM_TYPE_VIEW_ROUTE_NAVIGATION_STATE_KEY
  ] as RouteViewStateData | undefined;

  return navigationContext ?? readRouteViewState(routeStore, routeUrl);
}

/** Reads view state for the currently active RouteStore context. */
export function readCurrentRouteViewState(
  routeStore: RouteStoreInstance,
): RouteViewStateData | undefined {
  return routeStore.getCurrentContext()?.data as RouteViewStateData | undefined;
}

/** Grid row indices from RouteStore, or null when there is no saved view state. */
export function resolveSavedGridRowState(contextData?: RouteViewStateData): {
  activeRowIndex: number | null;
  expandedRowIndex: number | null;
  selectedRowIndexes?: number[];
} {
  return {
    activeRowIndex: contextData?.activeRow ?? null,
    expandedRowIndex: contextData?.expandedRow ?? null,
    selectedRowIndexes: contextData?.selectedRowIndexes?.length
      ? contextData.selectedRowIndexes
      : undefined,
  };
}

/** Captures the grid's current row selection for RouteStore. */
export function captureGridRowState(grid?: {
  activeRowIndex?: number | null;
  expandedRowIndex?: number | null;
  selectedRowIndexes?: Set<number>;
}): Pick<
  RouteViewStateData,
  "activeRow" | "expandedRow" | "selectedRowIndexes"
> {
  return {
    activeRow: grid?.activeRowIndex ?? null,
    expandedRow: grid?.expandedRowIndex ?? null,
    selectedRowIndexes: grid?.selectedRowIndexes?.size
      ? Array.from(grid.selectedRowIndexes)
      : undefined,
  };
}

/** Persists view state on the current RouteStore context before navigating away. */
export function saveRouteViewState(
  routeStore: RouteStoreInstance,
  data: RouteViewStateData,
): void {
  routeStore.updateCurrentContext({ data });
}
